import { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import lodashIsEmpty from 'lodash/isEmpty';

import { pluralize } from '../../../../Helper';
import DateHelper from '../../../../Helper/Date';
import MealPlanHelper from '../../../../Helper/MealPlan';
import useAlert, { getConfirmOptions } from '../../../../Hooks/useAlert';
import { updateMealPlanItemQuantity } from '../../../../RTK/mealPlan';
import {
  selectMealPlanCartByDay,
  selectMealPlanCartExceedMonday,
  selectMealPlanCounter,
} from '../../../../RTK/mealPlan/selectors';

const useMealPlanItemList = (day: number) => {
  const swipeRowRef = useRef({ current: {} });
  const alert = useAlert();
  const dispatch = useDispatch();
  const cart = useSelector((state) => selectMealPlanCartByDay(state, day));
  const cartExceedMonday = useSelector((state) =>
    selectMealPlanCartExceedMonday(state, day),
  );
  const cartCounter = useSelector((state) => selectMealPlanCounter(state, day));

  const note = useMemo(() => {
    const { isExceed, exceeding } = cartExceedMonday;
    if (day === 1) {
      return "All meal plan day carts must match Monday's total item quantity to proceed.";
    } else if (isExceed) {
      return `${DateHelper.convertNumberToDay(
        day,
      )}'s item count doesn't match Monday's plan. Please remove ${exceeding} ${pluralize(
        exceeding,
        'item',
      )} to proceed.`;
    }
    return null;
  }, [day, cartExceedMonday]);

  const _getKey = ({ prefix, item }: { prefix?: string; item: any }) => {
    const fstr = prefix ? `${prefix}.` : '';
    return lodashIsEmpty(item?.extras)
      ? `${fstr}${item?.id}`
      : `${fstr}${item?.id}.${item?.extras?.map?.((e) => e.id).join('-')}`;
  };

  // TODO - add proper data type when 'beta' branch is merged to 'main' branch
  const _onQtyChange = (data, quantity: number) => {
    dispatch(updateMealPlanItemQuantity({ day, data, quantity }));
  };

  // TODO - add proper data type when 'beta' branch is merged to 'main' branch
  const _onRemove = (data) => {
    alert(
      'Are you sure?',
      `Do you really want to remove ${data.name}?`,
      getConfirmOptions({ onConfirm: () => _removeMealPlanItem(data) }),
    );
  };

  // TODO - add proper data type when 'beta' branch is merged to 'main' branch
  const _removeMealPlanItem = (data) => {
    swipeRowRef?.current?.[_getKey({ item: data })]?.closeRow?.(); // close on mobile, just put optional chaining so it don't error on web
    MealPlanHelper.toggleMealPlanAutoNext(day);
    dispatch(updateMealPlanItemQuantity({ day, data, quantity: 0 })); // to update quantity to 0, means remove
  };

  const _onSwipeBegun = (itemId: string) => {
    // to close other open hidden items
    const refKeys = Object.keys(swipeRowRef.current).filter(
      (e) => e !== 'current' && e !== itemId.toString(), // exclude current key and this index key
    );
    refKeys.forEach((e) => {
      const ref = swipeRowRef.current[e];
      ref?.closeRow?.();
    });
  };

  return {
    swipeRowRef,
    cart,
    cartCounter,
    note,
    getKey: _getKey,
    onQtyChange: _onQtyChange,
    onRemove: _onRemove,
    onSwipeBegun: _onSwipeBegun,
  };
};

export default useMealPlanItemList;
